import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

export default function SignUp() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocale();

  const [form, setForm] = useState({ email: '', password: '', display_name: '', role: 'golfer' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token, user } = await api.auth.signup(form);
      login(token, user);
      navigate(user.role === 'caddie' ? '/onboarding' : '/feed');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">⛳</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('createAccount')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('joinToday')}</p>
        </div>

        <form onSubmit={handleSubmit} className="card p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('displayName')}</label>
            <input className="input" type="text" placeholder={t('yourName')} value={form.display_name} onChange={set('display_name')} required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('email')}</label>
            <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('password')}</label>
            <input className="input" type="password" placeholder={t('minPassword')} value={form.password} onChange={set('password')} required minLength={8} />
          </div>

          {/* Role selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('iAmA')}</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'golfer', emoji: '🏌️', label: t('golfer'), desc: t('golferDesc') },
                { value: 'caddie', emoji: '🎒', label: t('caddie'), desc: t('caddieDesc') },
              ].map((r) => (
                <label
                  key={r.value}
                  className={[
                    'cursor-pointer rounded-xl border-2 p-3 text-center transition-all',
                    form.role === r.value
                      ? 'border-brand-600 bg-brand-50'
                      : 'border-gray-200 hover:border-gray-300',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={form.role === r.value}
                    onChange={set('role')}
                    className="sr-only"
                  />
                  <div className="text-2xl mb-1">{r.emoji}</div>
                  <div className="font-semibold text-sm text-gray-800">{r.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{r.desc}</div>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? t('creatingAccount') : t('createAccount')}
          </button>

          <p className="text-center text-sm text-gray-500">
            {t('alreadyAccount')}{' '}
            <Link to="/signin" className="text-brand-700 font-medium hover:underline">{t('signIn')}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
